import { useEffect, useMemo, useState } from "react";
import { Lock, RotateCcw, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function RoleList({ roles, selected, onSelect }) {
  return (
    <div className="panel lg:w-64 shrink-0 overflow-hidden" data-testid="role-list">
      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Roller</div>
      {roles.map((r) => (
        <button key={r.key} onClick={() => onSelect(r.key)} data-testid={`role-item-${r.key}`}
          className={`w-full flex items-center justify-between px-3 py-2 text-left text-[13px] border-b border-slate-100 transition-colors ${selected === r.key ? "bg-blue-50 text-blue-800 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}>
          <span className="flex items-center gap-1.5">{r.is_super && <Lock className="h-3 w-3 text-slate-400" />}{r.name}</span>
          <span className="font-mono text-[11px] text-slate-400">{r.user_count}</span>
        </button>
      ))}
    </div>
  );
}

function MatrixTable({ schema, perms, disabled, onToggle }) {
  return (
    <div className="overflow-x-auto">
      <table className="erp-table" data-testid="permission-matrix">
        <thead><tr><th>Modül</th>{schema.actions.map((a) => <th key={a.key} className="!text-center">{a.label}</th>)}</tr></thead>
        <tbody>
          {schema.modules.map((m) => (
            <tr key={m.key}>
              <td className="font-medium text-slate-800">{m.label}</td>
              {schema.actions.map((a) => ((schema.action_modules?.[a.key] && !schema.action_modules[a.key].includes(m.key)) || (schema.module_actions?.[m.key] && !schema.module_actions[m.key].includes(a.key))) ? <td key={a.key} className="text-center text-slate-300">—</td> : (
                <td key={a.key} className="text-center">
                  <Checkbox checked={(perms[m.key] || []).includes(a.key)} disabled={disabled} onCheckedChange={() => onToggle(m.key, a.key)}
                    data-testid={`perm-${m.key}-${a.key}`} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function toggleAction(perms, module, action) {
  const cur = new Set(perms[module] || []);
  if (cur.has(action)) {
    cur.delete(action);
    if (action === "view") cur.clear();
  } else {
    cur.add(action);
    cur.add("view");
  }
  return { ...perms, [module]: [...cur] };
}

export default function RoleMatrix() {
  const { can, user, reload } = useAuth();
  const [schema, setSchema] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState("satis_personeli");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRoles = () => api.get("/roles").then(({ data }) => setRoles(data));
  useEffect(() => { api.get("/meta/permissions").then(({ data }) => setSchema(data)); loadRoles(); }, []);
  const role = useMemo(() => roles.find((r) => r.key === selected), [roles, selected]);
  useEffect(() => { if (role) setDraft({ permissions: role.permissions, flags: role.flags, data_scope: role.data_scope }); }, [role]);

  if (!schema || !role || !draft) return <div className="panel h-64 animate-pulse" />;
  const locked = role.is_super || !can("kullanicilar", "edit");

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/roles/${role.key}`, draft);
      toast.success(`${role.name} yetkileri kaydedildi (${data.changed_fields} değişiklik)`);
      await loadRoles();
      if (user.role_key === role.key) reload();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="role-matrix">
      <RoleList roles={roles} selected={selected} onSelect={setSelected} />
      <div className="panel flex-1 min-w-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900" data-testid="role-matrix-title">{role.name}</h3>
            <p className="text-xs text-slate-500">{role.description}</p>
          </div>
          {!locked && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setDraft({ permissions: role.permissions, flags: role.flags, data_scope: role.data_scope })} data-testid="role-reset-button"><RotateCcw className="h-3.5 w-3.5 mr-1" />Geri Al</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={save} data-testid="role-save-button">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Kaydet</Button>
            </div>
          )}
          {role.is_super && <span className="text-[11px] px-2 py-1 rounded bg-slate-100 border border-slate-200 text-slate-500" data-testid="role-locked-badge">Süper Yönetici tüm yetkilere sahiptir · düzenlenemez</span>}
        </div>
        <div className="flex flex-wrap gap-6 px-4 py-3 border-b border-slate-200 bg-slate-50/60" data-testid="role-flags">
          {schema.flags.map((fl) => (
            <label key={fl.key} className="flex items-center gap-2 text-[13px] font-medium text-slate-700">
              <Switch checked={!!draft.flags[fl.key]} disabled={locked} onCheckedChange={(v) => setDraft((d) => ({ ...d, flags: { ...d.flags, [fl.key]: v } }))} data-testid={`flag-${fl.key}`} />
              {fl.label}
            </label>
          ))}
          {(schema.data_scopes || []).map((ds) => (
            <label key={ds.key} className="flex items-center gap-2 text-[13px] font-medium text-slate-700">
              {ds.label}
              <Select value={draft.data_scope?.[ds.key] || "all"} disabled={locked} onValueChange={(v) => setDraft((d) => ({ ...d, data_scope: { ...d.data_scope, [ds.key]: v } }))}>
                <SelectTrigger className="h-8 w-48" data-testid={`scope-${ds.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ds.options.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </label>
          ))}
        </div>
        <MatrixTable schema={schema} perms={draft.permissions} disabled={locked} onToggle={(m, a) => setDraft((d) => ({ ...d, permissions: toggleAction(d.permissions, m, a) }))} />
      </div>
    </div>
  );
}
